import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ExternalLink, Pause, Play, Radio, Search, Square } from 'lucide-react';
import { Button, Chip, Select, TextField } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Loading, Page, Tabs, useNotice } from '../../components/common';
import { EnvPicker, useClusterEnvironments } from '../../components/envPicker';
import { logs, SEVERITIES, type LogEntry, type LogQuery, type Severity } from '../../api/logs';
import {
  buildLogQuery,
  ERRORS_ONLY,
  filtersToParams,
  PAGE_SIZES,
  parseLogParams,
  RANGES,
  timeWindow,
  toLocalInput,
  type LogFilters,
  type RangeKey,
} from '../../lib/logFilters';
import { errorMessage } from '../../lib/errors';
import { Histogram } from './Histogram';
import { LogTable } from './LogTable';

/** Live tail keeps at most this many entries in memory (newest first). */
export const TAIL_CAP = 2000;

const RANGE_LABEL: Record<RangeKey, string> = {
  '15m': '15m',
  '1h': '1h',
  '4h': '4h',
  '12h': '12h',
  '24h': '24h',
  '7d': '7d',
  custom: 'Custom',
};

interface Suppressed {
  reason: string;
  count: number;
  at: number;
}

export function LogsPage() {
  const [params, setParams] = useSearchParams();
  const paramsKey = params.toString();
  const applied = useMemo(() => parseLogParams(new URLSearchParams(paramsKey)), [paramsKey]);
  const [draft, setDraft] = useState<LogFilters>(applied);
  useEffect(() => setDraft(applied), [applied]);
  const set = <K extends keyof LogFilters>(k: K, v: LogFilters[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const { envs, loading: envsLoading, error: envsError } = useClusterEnvironments();
  const draftEnv = envs.find((e) => e.id === draft.env);
  const appliedEnv = envs.find((e) => e.id === applied.env);

  const notice = useNotice();
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [lastQuery, setLastQuery] = useState<LogQuery | null>(null);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [consoleUrl, setConsoleUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runSeq, setRunSeq] = useState(0);

  // live tail
  const [tailing, setTailing] = useState(false);
  const tailingRef = useRef(false);
  const tailAbort = useRef<AbortController | null>(null);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  const pending = useRef<LogEntry[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [suppressed, setSuppressed] = useState<Suppressed[]>([]);
  const [tailEnd, setTailEnd] = useState<string | null>(null);

  const queryAbort = useRef<AbortController | null>(null);

  // Run the query in the URL whenever it changes (shareable links auto-run), unless a tail owns the list.
  useEffect(() => {
    if (tailingRef.current || !applied.env || !appliedEnv) return;
    const q = buildLogQuery(applied, appliedEnv.namespaces);
    if (!q) {
      setError('The custom time range is incomplete or ends before it starts.');
      return;
    }
    queryAbort.current?.abort();
    const ac = new AbortController();
    queryAbort.current = ac;
    setLoading(true);
    setError(null);
    setTailEnd(null);
    setSuppressed([]);
    setLastQuery(q);
    logs
      .query(q, ac.signal)
      .then((page) => {
        if (ac.signal.aborted) return;
        setEntries(page.entries);
        setNextToken(page.nextPageToken);
        setConsoleUrl(page.consoleUrl || null);
      })
      .catch((e) => {
        if (ac.signal.aborted) return;
        setEntries([]);
        setNextToken(null);
        setError(errorMessage(e));
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [applied, appliedEnv, runSeq]);

  useEffect(() => () => tailAbort.current?.abort(), []);

  const apply = (f: LogFilters) => {
    const next = filtersToParams(f).toString();
    if (next === paramsKey) setRunSeq((n) => n + 1);
    else setParams(next);
  };

  const loadOlder = async () => {
    if (!lastQuery || !nextToken) return;
    setLoadingMore(true);
    try {
      const page = await logs.query({ ...lastQuery, pageToken: nextToken });
      setEntries((cur) => [...cur, ...page.entries]);
      setNextToken(page.nextPageToken);
    } catch (e) {
      notice.error(errorMessage(e));
    } finally {
      setLoadingMore(false);
    }
  };

  const setPausedBoth = (v: boolean) => {
    pausedRef.current = v;
    setPaused(v);
  };
  const flushPending = useCallback(() => {
    const add = pending.current;
    pending.current = [];
    setPendingCount(0);
    if (add.length) setEntries((cur) => [...add, ...cur].slice(0, TAIL_CAP));
  }, []);

  const stopTail = useCallback(() => {
    tailAbort.current?.abort();
    tailAbort.current = null;
    tailingRef.current = false;
    setTailing(false);
    pausedRef.current = false;
    setPaused(false);
    flushPending();
  }, [flushPending]);

  const startTail = () => {
    const env = envs.find((e) => e.id === draft.env);
    if (!env) return;
    const full = buildLogQuery({ ...draft, range: '1h' }, env.namespaces);
    if (!full) return;
    const { from: _f, to: _t, pageSize: _p, pageToken: _k, ...q } = full;
    queryAbort.current?.abort();
    setLoading(false);
    tailingRef.current = true;
    setTailing(true);
    setPausedBoth(false);
    pending.current = [];
    setPendingCount(0);
    setEntries([]);
    setNextToken(null);
    setConsoleUrl(null);
    setLastQuery(null);
    setSuppressed([]);
    setTailEnd(null);
    setError(null);
    const next = filtersToParams(draft).toString();
    if (next !== paramsKey) setParams(next);
    const ac = new AbortController();
    tailAbort.current = ac;
    logs
      .tail(
        q,
        (ev) => {
          if (ac.signal.aborted) return;
          switch (ev.event) {
            case 'entry':
              if (pausedRef.current) {
                pending.current = [ev.data, ...pending.current].slice(0, TAIL_CAP);
                setPendingCount(pending.current.length);
              } else setEntries((cur) => [ev.data, ...cur].slice(0, TAIL_CAP));
              break;
            case 'suppressed':
              setSuppressed((cur) => [{ ...ev.data, at: Date.now() }, ...cur].slice(0, 20));
              break;
            case 'error':
              notice.error(ev.data.error || 'Tail failed');
              break;
            case 'end':
              setTailEnd(ev.data.reason || 'ended');
              break;
          }
        },
        ac.signal
      )
      .catch((e) => {
        if (!ac.signal.aborted) notice.error(errorMessage(e));
      })
      .finally(() => {
        if (tailAbort.current === ac) {
          tailAbort.current = null;
          tailingRef.current = false;
          setTailing(false);
          pausedRef.current = false;
          setPaused(false);
          flushPending();
          setTailEnd((r) => r ?? (ac.signal.aborted ? null : 'stream closed'));
        }
      });
  };

  const allNs = draftEnv?.namespaces ?? [];
  const nsSelected = (n: string) => !draft.namespaces.length || draft.namespaces.includes(n);
  const toggleNs = (n: string) => {
    const cur = draft.namespaces.length ? draft.namespaces : allNs;
    const next = cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n];
    if (!next.length) return;
    set('namespaces', next.length === allNs.length ? [] : allNs.filter((x) => next.includes(x)));
  };
  const sevSelected = (s: Severity) => !draft.severities.length || draft.severities.includes(s);
  const toggleSev = (s: Severity) => {
    const cur = draft.severities.length ? draft.severities : [...SEVERITIES];
    const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s];
    if (!next.length) return;
    set('severities', next.length === SEVERITIES.length ? [] : SEVERITIES.filter((x) => next.includes(x)));
  };
  const errorsOnly =
    draft.severities.length === ERRORS_ONLY.length && ERRORS_ONLY.every((s) => draft.severities.includes(s));

  const setRange = (r: RangeKey) => {
    if (r === 'custom' && draft.range !== 'custom') {
      const w = timeWindow(draft);
      setDraft((d) => ({
        ...d,
        range: r,
        from: d.from || (w ? toLocalInput(new Date(w.from)) : ''),
        to: d.to || toLocalInput(new Date()),
      }));
    } else set('range', r);
  };

  const histoWindow = useMemo(() => {
    if (tailing || !lastQuery) return null;
    return { from: Date.parse(lastQuery.from), to: Date.parse(lastQuery.to) };
  }, [tailing, lastQuery]);

  const ran = !!lastQuery || tailing || !!tailEnd;

  return (
    <Page
      title="Logs"
      description="Cloud Logging for one environment's namespaces. Filters live in the URL, so a link reproduces the view."
      actions={
        consoleUrl && !tailing ? (
          <a
            className="nyk-ui-btn nyk-ui-btn-ghost nyk-ui-btn-sm nyk-btn-link"
            href={consoleUrl}
            target="_blank"
            rel="noreferrer"
          >
            <ExternalLink size={14} aria-hidden="true" /> Open in Cloud Console
          </a>
        ) : undefined
      }
    >
      {notice.element}
      <form
        className="nyk-card oph-log-filters"
        onSubmit={(e) => {
          e.preventDefault();
          if (tailing) stopTail();
          apply(draft);
        }}
      >
        <div className="oph-log-row">
          <div className="nyk-ui-field oph-log-env">
            <span>Environment</span>
            <EnvPicker
              envs={envs}
              value={draft.env}
              onChange={(id) => setDraft((d) => ({ ...d, env: id, namespaces: [] }))}
            />
          </div>
          <TextField
            label="Pod (regex)"
            value={draft.pod}
            maxLength={200}
            placeholder="^api-"
            onChange={(e) => set('pod', e.target.value)}
          />
          <TextField
            label="Container"
            value={draft.container}
            onChange={(e) => set('container', e.target.value)}
          />
          <TextField
            label="Text"
            value={draft.text}
            maxLength={500}
            placeholder="free text"
            onChange={(e) => set('text', e.target.value)}
          />
        </div>
        {envsLoading && <Loading loading />}
        <ErrorBox error={envsError} />
        {!envsLoading && !envs.length && !envsError && (
          <p className="nyk-card-intro">No enabled environment has a cluster yet (Admin → Environments).</p>
        )}
        {applied.env && !envsLoading && envs.length > 0 && !appliedEnv && (
          <ErrorBox error={`Unknown or disabled environment “${applied.env}”.`} />
        )}

        {draftEnv && (
          <div className="oph-log-group" role="group" aria-label="Namespaces">
            <span className="oph-log-label">Namespaces</span>
            <div className="nyk-chips">
              {allNs.map((n) => (
                <Chip key={n} size="sm" selected={nsSelected(n)} onClick={() => toggleNs(n)}>
                  {n}
                </Chip>
              ))}
            </div>
          </div>
        )}

        <div className="oph-log-group" role="group" aria-label="Severities">
          <span className="oph-log-label">Severity</span>
          <div className="nyk-chips">
            <Chip size="sm" selected={!draft.severities.length} onClick={() => set('severities', [])}>
              All
            </Chip>
            <Chip size="sm" selected={errorsOnly} onClick={() => set('severities', ERRORS_ONLY)}>
              Errors only
            </Chip>
            {SEVERITIES.map((s) => (
              <label key={s} className="nyk-check nyk-check-sm">
                <input type="checkbox" checked={sevSelected(s)} onChange={() => toggleSev(s)} />
                {s}
              </label>
            ))}
          </div>
        </div>

        <div className="oph-log-row oph-log-row-end">
          <div className="nyk-ui-field">
            <span>Time range</span>
            <Tabs
              value={draft.range}
              onChange={setRange}
              options={RANGES.map((r) => ({ value: r, label: RANGE_LABEL[r] }))}
            />
          </div>
          {draft.range === 'custom' && (
            <>
              <TextField
                label="From"
                type="datetime-local"
                value={draft.from}
                onChange={(e) => set('from', e.target.value)}
              />
              <TextField
                label="To"
                type="datetime-local"
                value={draft.to}
                onChange={(e) => set('to', e.target.value)}
              />
            </>
          )}
          <Select
            label="Page size"
            value={String(draft.pageSize)}
            onChange={(v) => set('pageSize', Number(v))}
            options={PAGE_SIZES.map((n) => ({ value: String(n), label: String(n) }))}
          />
          <div className="oph-log-buttons">
            <Button type="submit" icon={<Search size={16} />} disabled={!draftEnv} loading={loading}>
              Search
            </Button>
            {tailing ? (
              <Button variant="danger" icon={<Square size={14} />} onClick={stopTail}>
                Stop tail
              </Button>
            ) : (
              <Button variant="ghost" icon={<Radio size={16} />} disabled={!draftEnv} onClick={startTail}>
                Live tail
              </Button>
            )}
          </div>
        </div>
      </form>

      {(ran || loading || error) && (
        <div className="nyk-card oph-log-results">
          <div className="nyk-card-head">
            <div className="nyk-card-title-group">
              <h2>{tailing ? 'Live tail' : 'Results'}</h2>
              <span className="nyk-ui-hint" data-testid="log-count">
                {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
                {tailing && entries.length >= TAIL_CAP ? ` (newest ${TAIL_CAP} kept)` : ''}
              </span>
              {tailing && <span className="oph-live-dot" aria-label="live" />}
            </div>
            {tailing && (
              <div className="nyk-actions">
                {paused ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Play size={14} />}
                    onClick={() => {
                      setPausedBoth(false);
                      flushPending();
                    }}
                  >
                    Resume{pendingCount ? ` (${pendingCount} new)` : ''}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Pause size={14} />}
                    onClick={() => setPausedBoth(true)}
                  >
                    Pause
                  </Button>
                )}
              </div>
            )}
          </div>
          <ErrorBox error={error} />
          {suppressed.length > 0 && (
            <div className="oph-log-notes" role="status">
              {suppressed.slice(0, 3).map((s) => (
                <div key={s.at + s.reason} className="oph-log-note">
                  {s.count} entries suppressed by Cloud Logging ({s.reason})
                </div>
              ))}
            </div>
          )}
          {tailEnd && (
            <div className="oph-log-note" role="status" data-testid="tail-end">
              Tail ended: {tailEnd}
            </div>
          )}
          {entries.length > 0 && <Histogram entries={entries} window={histoWindow} />}
          <Loading loading={loading} />
          {!loading && !error && !entries.length && (
            <EmptyState
              title={tailing ? 'Waiting for entries…' : 'No log entries'}
              hint={tailing ? 'New entries appear at the top.' : 'Widen the time range or relax the filters.'}
            />
          )}
          {entries.length > 0 && <LogTable entries={entries} onCopied={notice.success} />}
          {!tailing && nextToken && (
            <div className="nyk-pager">
              <Button variant="ghost" size="sm" loading={loadingMore} onClick={() => void loadOlder()}>
                Load older
              </Button>
            </div>
          )}
        </div>
      )}
    </Page>
  );
}
